"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, MapPin, Clock, ShieldCheck, CircleCheck as CheckCircle2, Navigation, TriangleAlert as AlertTriangle, Radio, Car } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function ContactMap() {
  const shouldReduceMotion = useReducedMotion();
  const [activeRegion, setActiveRegion] = useState<string>("all");
  const regions = [{
    id: "md",
    state: "Maryland",
    hub: "Central Dispatch Hub: Bethesda / Silver Spring",
    counties: "Montgomery, Prince George's, Anne Arundel, Howard, Frederick, Baltimore",
    eta: "30-60 min dispatch"
  }, {
    id: "dc",
    state: "Washington DC",
    hub: "Metro Rapid Squad: Direct District Access",
    counties: "NW, NE, SW, SE (All Quadrants & Capitol Hill corridor)",
    eta: "25-45 min dispatch"
  }, {
    id: "nova",
    state: "Northern Virginia",
    hub: "NOVA Response Hub: Tysons / Alexandria",
    counties: "Fairfax, Arlington, Loudoun, Prince William, City of Alexandria",
    eta: "30-55 min dispatch"
  }];
  const mapHotspots = [{
    id: "dc-core",
    x: "49%",
    y: "52%",
    label: "Washington DC",
    state: "DC",
    code: "Metro Direct"
  }, {
    id: "md-moco",
    x: "43%",
    y: "38%",
    label: "Montgomery Co.",
    state: "MD",
    code: "Crews Active"
  }, {
    id: "md-pg",
    x: "57%",
    y: "45%",
    label: "Prince George's",
    state: "MD",
    code: "Staged Truck"
  }, {
    id: "md-aa",
    x: "68%",
    y: "34%",
    label: "Anne Arundel",
    state: "MD",
    code: "Standby Crew"
  }, {
    id: "md-balt",
    x: "62%",
    y: "20%",
    label: "Baltimore Area",
    state: "MD",
    code: "Regional Depot"
  }, {
    id: "va-arlington",
    x: "44%",
    y: "58%",
    label: "Arlington & Alex.",
    state: "VA",
    code: "Active Units"
  }, {
    id: "va-fairfax",
    x: "32%",
    y: "62%",
    label: "Fairfax Co.",
    state: "VA",
    code: "Staged Truck"
  }, {
    id: "va-loudoun",
    x: "24%",
    y: "44%",
    label: "Loudoun Co.",
    state: "VA",
    code: "Crews Active"
  }];
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="contact-info-map" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Deep structural container holding the full emergency dispatch briefing */}
        <div className="bg-secondary text-secondary-foreground rounded-2xl border border-border/40 p-6 md:p-10 lg:p-12 shadow-2xl relative overflow-hidden">
          {/* Subtle industrial blueprint grid background overlay */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[radial-gradient(ellipse_at_center,_var(--color-accent)_0%,_transparent_70%)]" aria-hidden="true" />

          {/* Top operational telemetry bar */}
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-secondary-foreground/15 pb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/20 px-3.5 py-1 text-xs font-mono uppercase tracking-wide text-secondary-foreground">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-primary" />
              </span>
              <span>24/7 Live Regional Emergency Dispatch</span>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono text-secondary-foreground/80">
              <span className="flex items-center gap-1.5">
                <Radio className="size-3.5 text-accent" />
                TriServe Local Fleets On-Call
              </span>
              <span className="hidden sm:inline text-secondary-foreground/40">•</span>
              <span className="hidden sm:inline">Zero Call Centers — Direct Technicians</span>
            </div>
          </div>

          {/* Main 2-column layout: Info on left, Interactive Map on right */}
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-start">
            {/* Left Column: Direct Call, H1, and Regional Territories */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="lg:col-span-6 flex flex-col space-y-6">
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-secondary-foreground leading-tight">
                  Get direct 24/7 emergency dispatch across MD, DC, and Northern Virginia
                </h1>
                <p className="mt-4 text-base sm:text-lg text-secondary-foreground/80 leading-relaxed">
                  Call our duty supervisor right now. You speak directly with local field technicians who mobilize equipment trucks immediately to protect your home or business.
                </p>
              </div>

              {/* Oversized Click-to-Call Emergency Action Box */}
              <div className="rounded-xl border border-accent/40 bg-secondary/80 p-5 sm:p-6 backdrop-blur-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <span className="text-xs font-mono uppercase tracking-wider text-accent font-semibold flex items-center gap-1.5">
                      <AlertTriangle className="size-3.5" />
                      Immediate Emergency Hotline
                    </span>
                    <div className="text-2xl sm:text-3xl font-black tracking-tight text-secondary-foreground font-mono">
                      855-435-6631
                    </div>
                    <p className="text-xs text-secondary-foreground/70">
                      Disaster crew rolls within minutes • Water, fire, mold, sewage
                    </p>
                  </div>

                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-6 py-6 text-base shadow-lg transition-transform active:scale-98">
                    <a href="tel:8554356631" className="inline-flex items-center gap-2">
                      <PhoneCall className="size-5" />
                      <span>Call 855-435-6631</span>
                    </a>
                  </Button>
                </div>
              </div>

              {/* Verified Field Crew Banner */}
              <div className="overflow-hidden rounded-xl border border-secondary-foreground/15 bg-secondary/60">
                <div className="relative aspect-[16/7] w-full">
                  <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_response_crew_truck.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_response_crew_truck.png-wvc-sizes" alt="TriServe certified mitigation technicians in uniform beside mobile extraction truck" loading="lazy" decoding="async" className="h-full w-full object-cover" src="https://triserveinc.com/wp-content/uploads/2025/11/Triserve-Crew.png" />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/40 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs font-mono text-secondary-foreground">
                    <span className="bg-secondary/90 px-2.5 py-1 rounded border border-border/30 backdrop-blur-xs flex items-center gap-1.5">
                      <Car className="size-3 text-accent" />
                      Unit #44 Mobile Desiccant Drying Fleet
                    </span>
                    <span className="hidden sm:inline bg-primary/90 text-primary-foreground px-2 py-1 rounded font-semibold">
                      En Route Standard: Under 60 Min
                    </span>
                  </div>
                </div>
              </div>

              {/* Service Territory Breakdown */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-secondary-foreground/90 font-mono">
                    TriServe Primary Service Territories
                  </h2>
                  <span className="text-xs text-accent font-mono font-medium">All Local Crews</span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {regions.map((region, i) => <div key={region.id} data-index={i} onClick={() => setActiveRegion(region.id)} className={`cursor-pointer rounded-lg border p-4 transition-[border-color,background-color] ${activeRegion === region.id ? "border-accent bg-secondary/90 ring-1 ring-accent/60" : "border-secondary-foreground/15 bg-secondary/40 hover:border-secondary-foreground/30"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="size-4 text-accent shrink-0" />
                          <span className="font-bold text-base text-secondary-foreground">
                            {region.state}
                          </span>
                        </div>
                        <span className="text-xs font-mono bg-primary/20 text-secondary-foreground border border-primary/40 px-2 py-0.5 rounded">
                          {region.eta}
                        </span>
                      </div>
                      <p className="mt-2 text-xs sm:text-sm text-secondary-foreground/90 font-medium">
                        {region.counties}
                      </p>
                      <p className="mt-1 text-xs font-mono text-secondary-foreground/60">
                        {region.hub}
                      </p>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Right Column: Interactive TriServe Regional Dispatch Area Map */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="lg:col-span-6 flex flex-col space-y-4">
              <Card className="rounded-xl border border-secondary-foreground/20 bg-secondary/90 text-secondary-foreground overflow-hidden py-0 shadow-xl">
                {/* Map Header bar */}
                <div className="flex flex-wrap items-center justify-between border-b border-secondary-foreground/15 p-4 bg-secondary">
                  <div className="flex items-center gap-2">
                    <Navigation className="size-4 text-accent" />
                    <span className="font-bold text-sm tracking-wide">
                      Mid-Atlantic Coverage Radar
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setActiveRegion("all")} className={`h-auto text-xs font-mono px-2.5 py-1 rounded transition-colors ${activeRegion === "all" ? "bg-accent text-accent-foreground font-bold hover:bg-accent hover:text-accent-foreground" : "bg-secondary-foreground/10 text-secondary-foreground hover:bg-secondary-foreground/20 hover:text-secondary-foreground"}`}>
                      Reset View
                    </Button>
                    <span className="text-xs font-mono text-primary-foreground bg-primary px-2.5 py-1 rounded font-semibold">
                      Live Area Map
                    </span>
                  </div>
                </div>

                <CardContent className="p-0">
                  {/* Interactive Map Visual Representation */}
                  <div className="relative w-full aspect-[4/3] bg-secondary/95 overflow-hidden select-none border-b border-secondary-foreground/15">
                    {/* Stylized GIS geographic grid & topography lines */}
                    <svg className="absolute inset-0 h-full w-full stroke-secondary-foreground/15" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <defs>
                        <pattern id="gis-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M 40 0 L 0 0 0 40" fill="none" strokeWidth="0.8" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#gis-grid)" />

                      {/* Potomac & Chesapeake regional waterway paths */}
                      <path d="M 120 40 Q 220 180 280 230 T 420 380" fill="none" stroke="hsl(198 80% 38%)" strokeWidth="3.5" strokeDasharray="6 4" className="opacity-70" />
                      <path d="M 380 40 Q 420 140 460 210 T 520 380" fill="none" stroke="hsl(198 80% 38%)" strokeWidth="5" className="opacity-50" />
                    </svg>

                    {/* Regional Coverage Zone Boundaries */}
                    <div className="absolute inset-4 rounded-xl border border-dashed border-accent/40 bg-accent/5 pointer-events-none">
                      <span className="absolute top-2 left-2 text-[10px] font-mono text-accent uppercase font-bold tracking-wider">
                        TriServe Direct Dispatch Perimeter
                      </span>
                    </div>

                    {/* Interactive Hotspots */}
                    {mapHotspots.map((spot, i) => {
                    const isHighlighted = activeRegion === "all" || activeRegion === "md" && spot.state === "MD" || activeRegion === "dc" && spot.state === "DC" || activeRegion === "nova" && spot.state === "VA";
                    return <div key={spot.id} data-index={i} style={{
                      left: spot.x,
                      top: spot.y
                    }} className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 group ${isHighlighted ? "opacity-100 scale-100" : "opacity-35 scale-90"}`} onClick={() => {
                      if (spot.state === "MD") setActiveRegion("md");
                      if (spot.state === "DC") setActiveRegion("dc");
                      if (spot.state === "VA") setActiveRegion("nova");
                    }}>
                          <div className="relative flex items-center justify-center">
                            <span className={`absolute size-7 rounded-full animate-ping ${spot.state === "DC" ? "bg-accent/40" : isHighlighted ? "bg-primary/40" : "hidden"}`} />
                            <div className={`relative size-4 rounded-full border-2 flex items-center justify-center ${spot.state === "DC" ? "bg-accent border-accent-foreground" : isHighlighted ? "bg-primary border-secondary-foreground" : "bg-secondary-foreground/40 border-secondary"}`}>
                              <div className="size-1.5 rounded-full bg-secondary-foreground" />
                            </div>
                          </div>

                          {/* Marker Callout Tag */}
                          <div className="mt-1.5 whitespace-nowrap rounded bg-secondary px-2 py-0.5 text-center text-[11px] font-mono font-bold text-secondary-foreground shadow-md border border-secondary-foreground/20 group-hover:border-accent">
                            <div>{spot.label}</div>
                            <div className="text-[9px] text-accent font-normal">{spot.code}</div>
                          </div>
                        </div>;
                  })}

                    {/* Bottom Status ticker inside map */}
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-md bg-secondary/90 px-3 py-2 text-xs font-mono border border-secondary-foreground/20 backdrop-blur-xs">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="size-3.5 text-primary" />
                        <span>TriServe Dedicated Service Area</span>
                      </div>
                      <span className="text-accent font-bold">100% Direct Response</span>
                    </div>
                  </div>

                  {/* Operational Verification Checklist */}
                  <div className="p-4 sm:p-5 space-y-3 bg-secondary/80">
                    <h3 className="text-xs font-mono uppercase tracking-wider text-secondary-foreground/70 font-semibold">
                      Disaster Response Protocols
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-secondary-foreground/90">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Direct insurance adjuster coordination</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Water extraction & drying equipment on-board</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Certified biohazard & mold remediation teams</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Full turn-key structural rebuilding licensed</span>
                      </div>
                    </div>

                    <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-secondary-foreground/15">
                      <div className="text-xs text-secondary-foreground/70">
                        <Clock className="size-3.5 inline mr-1 text-accent" />
                        Crews stationed across MD, DC, and NOVA right now.
                      </div>
                      <Button asChild variant="outline" size="sm" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground text-xs font-mono font-bold">
                        <a href="tel:8554356631">
                          Speak With Dispatcher
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}